import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Home, Calendar, TrendingUp,
  FileText, CreditCard, CheckSquare, BarChart3, LogOut,
  Phone, ClipboardList, UserCog, Building2, Mail,
} from "lucide-react";
import "../css/sidebar.css";
import ZenLogo from "../assets/images/ZEN LOGO-w 1.png";
import api from "../api/api";

// ─── Navigation groups per role ──────────────────────────────────────────

const CORE_MENU = [
  { name: "Dashboard",    path: "/dashboard",   icon: LayoutDashboard },
  { name: "Leads",        path: "/leads",        icon: Users },
  { name: "Properties",   path: "/properties",   icon: Building2 },
  { name: "Site Visits",  path: "/site-visits",  icon: Calendar },
  { name: "Deals",        path: "/deals",        icon: TrendingUp },
];

const OPS_MENU = [
  { name: "Payments",  path: "/payments",  icon: CreditCard },
  { name: "Documents", path: "/documents", icon: FileText },
  { name: "Tasks",     path: "/tasks",     icon: CheckSquare },
  { name: "Call Logs", path: "/call-logs", icon: Phone },
];

const REPORTS_MENU = [
  { name: "Analytics",        path: "/analytics",      icon: BarChart3 },
  { name: "MIS Report",       path: "/mis",            icon: ClipboardList },
  { name: "Reports & Digest", path: "/reports-digest", icon: Mail, roles: ["admin", "superadmin"] },
];

const ADMIN_MENU = [
  { name: "User Management", path: "/users", icon: UserCog },
];

// Which groups each role sees
const ROLE_GROUPS = {
  superadmin: ["core", "ops", "reports", "admin"],
  admin:      ["core", "ops", "reports"],
  agent:      ["core", "ops", "reports"],
};

const ROLE_LABEL = {
  superadmin: "Super Admin",
  admin:      "Manager",
  agent:      "Agent",
};

export default function Sidebar({ open, onClose }) {
  const location = useLocation();
  const navigate  = useNavigate();

  const [user, setUser] = useState({ name: "", email: "", role: "agent" });

  useEffect(() => {
    const storedRole  = localStorage.getItem("role")  || "agent";
    const storedName  = localStorage.getItem("name")  || "";
    const storedEmail = localStorage.getItem("email") || "";
    setUser({ name: storedName, email: storedEmail, role: storedRole });

    // Refresh from API in background
    api.get("/users/me")
      .then(res => setUser({
        name:  res.data.name  || storedName,
        email: res.data.email || storedEmail,
        role:  res.data.role  || storedRole,
      }))
      .catch(() => {});
  }, []);

  const role   = user.role || "agent";
  const groups = ROLE_GROUPS[role] || ROLE_GROUPS.agent;

  const isActive = (path) => location.pathname === path;

  const renderItems = (items) =>
    items
      .filter((item) => !item.roles || item.roles.includes(role))
      .map((item) => (
      <Link
        key={item.name}
        to={item.path}
        onClick={() => onClose?.()}
        className={`nav-item ${isActive(item.path) ? "active" : ""}`}
      >
        <item.icon size={17} className="nav-icon" />
        {item.name}
      </Link>
    ));

  return (
    <>
      {open && <div className="overlay" onClick={onClose} />}

      <aside className={`sidebar ${open ? "open" : ""}`}>
        {/* ── Logo ── */}
        <div className="sidebar-header">
          <img src={ZenLogo} alt="Zen Realty" className="sidebar-logo" />
        </div>

        {/* ── Navigation ── */}
        <nav className="menu">
          {groups.includes("core") && (
            <>
              <p className="menu-group-label">Main</p>
              {renderItems(CORE_MENU)}
            </>
          )}

          {groups.includes("ops") && (
            <>
              <p className="menu-group-label">Operations</p>
              {renderItems(OPS_MENU)}
            </>
          )}

          {groups.includes("reports") && (
            <>
              <p className="menu-group-label">Reports</p>
              {renderItems(REPORTS_MENU)}
            </>
          )}

          {groups.includes("admin") && (
            <>
              <p className="menu-group-label">Administration</p>
              {renderItems(ADMIN_MENU)}
            </>
          )}
        </nav>

        {/* ── User footer ── */}
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="avatar">
              {(user.name || "U").charAt(0).toUpperCase()}
            </div>
            <div className="user-meta">
              <p className="username">{user.name || "User"}</p>
              <span className={`user-role-badge ${role}`}>
                {ROLE_LABEL[role] || role}
              </span>
            </div>
          </div>

          <button
            className="logout-btn"
            title="Logout"
            onClick={() => {
              localStorage.clear();
              navigate("/");
            }}
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>
    </>
  );
}
